import router from '@/router'
import { authServices } from '../services/auth'
import { applyThemeClass, persistTheme, THEME_DARK, THEME_LIGHT } from '@/utils/theme'
import { licenseServices } from '@/services/license'
import { dbServices } from '@/services/db'
import common from '@/utils/common'

let settingRequest = null

function hasUsableAsrSetting(setting) {
    if (!setting) return false
    return common.isValidASRUrl(common.getASRUrl(
        setting.asr_domain,
        setting.asr_ws_port,
        setting.asr_ws_path,
    ))
}

export const actions = {
    setLoading ({commit}, payload) {
        commit('setLoading', payload)
    },
    // KAN-95 暗室暗色主題：使用者主動切換（更新 state + 套 root class + 持久化）
    setUiTheme ({ commit }, theme) {
        const next = theme === THEME_DARK ? THEME_DARK : THEME_LIGHT
        commit('SET_UI_THEME', next)
        applyThemeClass(next)
        persistTheme(next)
    },
    // 在亮 / 暗之間切換
    toggleUiTheme ({ state, dispatch }) {
        const next = state.ui.theme === THEME_DARK ? THEME_LIGHT : THEME_DARK
        dispatch('setUiTheme', next)
    },
    setLoadingDescript ({commit}, payload) {
        commit('setLoadingDescript', payload)
    },
    setUser ({ commit }, user) {
        commit('setUser', user)
    },
    async login ({ commit, dispatch }, payload) {
        commit('setLoading', true)

        try {
            const res = await authServices.signIn(payload.username, payload.password)

            if (res.result === 'success') {
                dispatch('setUser', res)
                // 首次匿名啟動會拿不到受保護的 setting；登入完成後必須在換頁前重試。
                await dispatch('ensureSetting').catch(() => null)
                const runtime = await dispatch('refreshLicenseRuntime')
                commit('setError', null)

                router.push(runtime.effective_status === 'suspended' ? '/history-export' : '/home')
            } else {
                commit('setError', 'auth')
            } 
        } catch (e) {
            void 0
        } finally {
            commit('setLoading', false)
        }
    },
    async logout ({ dispatch, commit }) {
        commit('setLoading', true)
        try {
            await authServices.signOut()
        } finally {
            // 共用工作站無論網路結果都立即鎖回登入畫面；server 端在 destroy 前先撤銷 session。
            dispatch('setUser', null)
            window.location.replace('/')
        }
    },
    handleIsAutoRecognize ({ commit }, payload) {
        commit('setIsAutoRecognize', payload)
    },
    setSetting({commit}, setting) {
        commit('SET_SETTING', setting)
    },
    ensureSetting({ commit, state }) {
        if (hasUsableAsrSetting(state.setting)) return Promise.resolve(state.setting)

        if (!settingRequest) {
            settingRequest = dbServices.get_setting()
                .then(res => {
                    if (!res || !res.success || !hasUsableAsrSetting(res.data)) {
                        throw new Error('ASR_SETTING_UNAVAILABLE')
                    }
                    commit('SET_SETTING', res.data)
                    return res.data
                })
                .finally(() => {
                    // 失敗不得永久快取，登入後或下一個受保護入口必須能重新載入。
                    settingRequest = null
                })
        }
        return settingRequest
    },
    // 醫院別配置注入（main.js mount 前呼叫）
    setHospital({commit}, hospital) {
        commit('SET_HOSPITAL', hospital)
    },
    async refreshLicenseRuntime({ commit, state }) {
        try {
            const previous = state.license.effective_status
            const runtime = await licenseServices.runtime()
            commit('SET_LICENSE', runtime)
            if (previous !== 'active' && runtime.effective_status === 'active') {
                commit('SET_LICENSE_RESTRICTION_NOTICE', { type: 'restored' })
            }
            return runtime
        } catch (error) {
            // server 無法取得時保留最後狀態；首次啟動的預設值與 server 首次 fail-open 一致。
            return state.license
        }
    },
    showLicenseRestriction({ commit }, capability) {
        commit('SET_LICENSE_RESTRICTION_NOTICE', { type: 'restricted', capability })
    },
    clearLicenseRestriction({ commit }) {
        commit('SET_LICENSE_RESTRICTION_NOTICE', null)
    },
}
